import findIndexByInstanceKey from './findIndexByInstanceKey';
import findIndexToInsertAt from './findIndexToInsertAt';
import setRowOperation from './setRowOperation';
import removeRowsFromListViewStore from 'owa-mail-actions/lib/triage/removeRowsFromListViewStore';
import updateRollupOnAddOrUpdateRowNotification from 'owa-mail-focused-inbox-rollup-store/lib/actions/updateRollupOnAddOrUpdateRowNotification';
import { isInboxPlusEnabled } from 'owa-mail-inbox-plus-store/lib/selectors/inboxPlusFlightUtil';
import {
    getFirstUnpinnedRowIndex,
    fetchRichContentForRows,
} from 'owa-mail-list-response-processor';
import { getIsPinnedTimestamp } from 'owa-mail-list-store';
import TableOperations from 'owa-mail-list-table-operations';
import { isNudgedRow } from 'owa-mail-nudge-store';
import { lazyPrefetchSingleRow } from 'owa-mail-prefetch';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { action } from 'satcheljs/lib/legacy';
import { getTimestamp } from 'owa-datetime';
import {
    preProcessServerRowPayload,
    isRowSortKeyEqual,
    getDateSortTimeStampFromRowData,
} from 'owa-mail-list-response-processor';
import {
    getFocusedFilterForTable,
    MailListRowDataType,
    RowOperation,
    TableView,
    isFolderPaused,
    listViewStore,
} from 'owa-mail-list-store';

/**
 * Performs update for a row that already exists on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt the index to insert the row at
 */
function updateExistingRow(
    row: MailListRowDataType,
    tableView: TableView,
    indexToInsertAt: number
) {
    const isSortKeyEqual = isRowSortKeyEqual(row, tableView);
    if (isSortKeyEqual) {
        // Only update data if sort key is the same
        TableOperations.updateRowData(row, tableView);
        setRowOperation(tableView, RowOperation.RowUpdateInPlace);
    } else {
        // Update data and row position otherwise
        TableOperations.updateRow(indexToInsertAt, row, tableView);
        setRowOperation(tableView, RowOperation.RowMove);
    }
}

/**
 * Inserts a new row that does not exist on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt - the index to insert the row at
 */
function addNewRow(row: MailListRowDataType, tableView: TableView, indexToInsertAt: number) {
    TableOperations.addRow(indexToInsertAt, row, tableView);
    setRowOperation(tableView, RowOperation.RowAdd);

    // Also increment current loaded index so the new row will be guaranteed to show up in the view
    // without bumping out an existing item
    tableView.currentLoadedIndex++;
}

/**
 * This function handles rowAdd and rowModified notifications.
 * @param row present in the notification
 * @param tableView for which notification has arrived
 */
export default action('rowNotificationHandler.onAddOrUpdateRow')(function onAddOrUpdateRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    const instanceKey = row.InstanceKey;
    if (!instanceKey) {
        throw new Error('onAddOrUpdateNotification: Row instanceKey should not be null');
    }

    // Pre-process server row and determine whether to consume it further
    const shouldProcessPayload = preProcessServerRowPayload(row, tableView);
    if (!shouldProcessPayload) {
        return;
    }

    // Compute the insert index by passing in the lastDeliveryOrRenewTimeStamp and lastDeliveryTimeStamp.
    // Cannot use MailRowDataPropertyGetter here because the row hasn't been saved to item cache yet.
    const { lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp } = getDateSortTimeStampFromRowData(
        row,
        tableView.tableQuery.listViewType
    );

    let indexToInsertAt = findIndexToInsertAt(
        lastDeliveryOrRenewTimeStamp,
        lastDeliveryTimeStamp,
        tableView,
        0 /* startIndex */,
        true /*skipNudgedRows*/
    );

    const indexOfElement = findIndexByInstanceKey(instanceKey, tableView);
    const isRowAlreadyPresent = indexOfElement >= 0;

    if (isRowAlreadyPresent && indexOfElement < indexToInsertAt) {
        // If we're moving an existing item down in the list, decrease index to insert by 1 to account for the row being
        // removed from above the indexToInsertAt
        indexToInsertAt--;
    }

    if (indexToInsertAt == -1) {
        if (isRowAlreadyPresent) {
            // Remove the row if could not place it, because the row could have moved down (and out of loaded range) for any reason from another client,
            // e.g unpin a row or delete the latest item in the conversation from another client
            removeRowsFromListViewStore([instanceKey], tableView);
        }

        // Ignore the notification if we could not find an index to insert it
        return;
    }

    // Do not add row if inbox is paused and the time of the message is after the time the user paused. However, messages
    // with an earlier timestamp like those that were deleted and then undoed or older messages moved in from another folder should be allowed in.
    if (
        isFolderPaused(tableView.tableQuery.folderId) &&
        new Date(lastDeliveryTimeStamp).getTime() > getTimestamp(listViewStore.inboxPausedDateTime)
    ) {
        return;
    }

    // cannot call isUnpinnedNudgedRow because row may not have been saved to store yet
    const isUnpinnedNudgedRow =
        isNudgedRow(row.InstanceKey, tableView.id) &&
        !getIsPinnedTimestamp(lastDeliveryOrRenewTimeStamp);

    if (isUnpinnedNudgedRow) {
        // always recalculate as it's possible an existing row has just been unpinned
        indexToInsertAt = getFirstUnpinnedRowIndex(tableView, 0);
    }

    if (isRowAlreadyPresent) {
        updateExistingRow(row, tableView, indexToInsertAt);
    } else {
        addNewRow(row, tableView, indexToInsertAt);
    }

    // Always pre-fetch if we're getting a row update because we don't want an entry in our data cache to go stale
    lazyPrefetchSingleRow.importAndExecute(instanceKey, tableView, true /* updateIfExists */);

    // Update focused inbox rollup data for Focused/Other notifications
    if (getFocusedFilterForTable(tableView) != FocusedViewFilter.None && !isInboxPlusEnabled()) {
        updateRollupOnAddOrUpdateRowNotification(tableView);
    }

    // FetchRichContentForRows
    fetchRichContentForRows(tableView);
});
